/**
 * Module Studio — import de fichiers Markdown comme documents de spec (retour d'Olivier du 14.10, UPT-2103 n° 1).
 *
 * Fonctions pures : à partir du nom et du texte d'un fichier `.md`, propose le type (rest / web / shared / report),
 * le titre (premier titre `#`, sinon le nom sans extension) et le chemin dans le dépôt (`<type>/<nom>.md`,
 * conforme à `DESIGN_LIMITS.specPathPattern`, suffixé `-2`, `-3`… s'il est déjà pris).
 */
import { DESIGN_LIMITS, type DesignSpecDocumentInput, type DesignSpecKind } from '../data/contracts';

const MARKDOWN_EXTENSIONS = ['.md', '.markdown'];
const KIND_FOLDERS: Record<DesignSpecKind, string> = { rest: 'rest', web: 'web', shared: '_shared', report: 'reports' };
const DEFAULT_KIND: DesignSpecKind = 'shared';
const TITLE_PATTERN = /^#\s+(.+?)\s*$/m;
const EXTENSION = '.md';
const SUFFIX_SEPARATOR = '-';

export interface MarkdownImportPlan {
  kind: DesignSpecKind;
  title: string;
  path: string;
}

export const isMarkdownFileName = (fileName: string): boolean =>
  MARKDOWN_EXTENSIONS.some((extension) => fileName.toLowerCase().endsWith(extension));

/** Type déduit du nom (`…-rest.md`, `web/…`, `report`, `rapport`) ; `shared` à défaut. */
export const guessSpecKind = (fileName: string): DesignSpecKind => {
  const lower = fileName.toLowerCase();
  if (/(^|[\\/_.-])(rest|api|backend)([\\/_.-]|$)/.test(lower)) return 'rest';
  if (/(^|[\\/_.-])(web|front|frontend|ui)([\\/_.-]|$)/.test(lower)) return 'web';
  if (/(^|[\\/_.-])(report|reports|rapport)([\\/_.-]|$)/.test(lower)) return 'report';
  return DEFAULT_KIND;
};

const baseName = (fileName: string): string => {
  const last = fileName.split(/[\\/]/).pop() ?? fileName;
  return last.replace(/\.(md|markdown)$/i, '');
};

/** Segment de chemin conforme au motif des specs : lettres, chiffres, `.`, `_`, `-`. */
export const sanitizePathSegment = (value: string): string => {
  const cleaned = value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return cleaned.length > 0 ? cleaned : 'spec';
};

export const guessSpecTitle = (fileName: string, text: string): string => {
  const match = TITLE_PATTERN.exec(text);
  const title = (match ? match[1] : baseName(fileName)).trim();
  return title.slice(0, DESIGN_LIMITS.specTitleMaxChars);
};

/** Chemin libre parmi `taken` : `<dossier>/<nom>.md`, puis `-2`, `-3`… */
export const freeSpecPath = (kind: DesignSpecKind, fileName: string, taken: ReadonlySet<string>): string => {
  const folder = KIND_FOLDERS[kind];
  const stem = sanitizePathSegment(baseName(fileName));
  const maxStem = DESIGN_LIMITS.specPathMaxChars - folder.length - 1 - EXTENSION.length - 4;
  const shortStem = stem.slice(0, Math.max(1, maxStem));
  let candidate = `${folder}/${shortStem}${EXTENSION}`;
  let index = 2;
  while (taken.has(candidate)) {
    candidate = `${folder}/${shortStem}${SUFFIX_SEPARATOR}${index}${EXTENSION}`;
    index += 1;
  }
  return candidate;
};

export const planMarkdownImport = (fileName: string, text: string, taken: ReadonlySet<string>): MarkdownImportPlan => {
  const kind = guessSpecKind(fileName);
  return { kind, title: guessSpecTitle(fileName, text), path: freeSpecPath(kind, fileName, taken) };
};

export const toSpecInput = (plan: MarkdownImportPlan, text: string): DesignSpecDocumentInput => ({
  kind: plan.kind,
  title: plan.title,
  path: plan.path,
  contentMd: text,
});

export const isMarkdownTooLarge = (text: string): boolean =>
  new TextEncoder().encode(text).length > DESIGN_LIMITS.specContentMaxBytes;
